import React, { useState } from 'react';
import FontAwesomeIcon from './FontAwesomeIcon';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);

    try {
      // Prepare data for Netlify Forms
      const formPayload = new URLSearchParams();
      formPayload.append('form-name', 'contact');
      formPayload.append('bot-field', ''); // Honeypot field
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone || '');
      formPayload.append('subject', formData.subject);
      formPayload.append('message', formData.message);

      console.log('Submitting form data:', formPayload.toString());

      const response = await fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formPayload.toString(),
        redirect: 'manual' // Handle redirects manually
      });

      console.log('Response status:', response.status);
      console.log('Response type:', response.type);
      console.log('Response URL:', response.url);
      console.log('Response headers:', [...response.headers.entries()]);

      // Netlify Forms returns 200, 303, or other 3xx redirects on success
      if (response.ok || response.status === 303 || (response.status >= 300 && response.status < 400)) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        const responseText = await response.text();
        console.error('Server response:', response.status, response.statusText, responseText);
        throw new Error(`Form submission failed: ${response.status} - ${responseText}`);
      }
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      console.error('Form submission error:', err);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon="fa-check" className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-green-900 mb-2">
          Thank You for Contacting Us!
        </h3>
        <p className="text-green-700 mb-6">
          We've received your message and will get back to you within 24 hours.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="btn-primary inline-block"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FontAwesomeIcon icon="fa-exclamation-circle" className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
            placeholder="john@example.com"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
            placeholder="+1 234 567 8900"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Subject *
          </label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition"
          >
            <option value="">Select a subject</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Safari Booking">Safari Booking</option>
            <option value="Custom Itinerary">Custom Itinerary Request</option>
            <option value="Group Travel">Group Travel</option>
            <option value="Gorilla Permits">Gorilla Permit Booking</option>
            <option value="Accommodation">Accommodation Question</option>
            <option value="Transportation">Transportation Services</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Message *
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows="6"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold focus:border-transparent transition resize-none"
          placeholder="Tell us about your dream Uganda safari..."
        ></textarea>
      </div>

      <button type="submit" className="btn-primary w-full md:w-auto px-12 py-4">
        Send Message
        <FontAwesomeIcon icon="fa-paper-plane" className="w-5 h-5 ml-2 inline" />
      </button>

      <p className="text-xs text-gray-500">
        * Required fields. We respect your privacy and will never share your information.
      </p>
    </form>
  );
}
